import { PencilIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";

import * as InlineEdit from "@/components/InlineEdit";
import { Title } from "@/components/ui/ResourceHeader/Title";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

const MAX_TITLE_LENGTH = 256;
const CREATE_PLACEHOLDER = "New signal";

interface SignalFormTitleProps {
  mode: "create" | "edit";
  title: string;
  onTitleEdit?: (params: {
    newTitle: string;
    onSuccess?: (newTitle?: string) => void;
    onError?: () => void;
  }) => void;
  onCreateNameChange: (name: string) => void;
}

const getTitleClasses = (titleText: string) => {
  const baseClasses = "font-semibold truncate";
  const sizeClasses = titleText.length > 30 ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl";
  return `${baseClasses} ${sizeClasses}`.trim();
};

const CreateModeTitle = ({
  title,
  onCreateNameChange,
}: {
  title: string;
  onCreateNameChange: (name: string) => void;
}) => {
  const displayTitle = title.trim() || CREATE_PLACEHOLDER;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }
    const timer = setTimeout(() => setErrorMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  const handleEdit = (newTitle: string) => {
    const trimmed = newTitle.trim();

    if (!trimmed) {
      setErrorMessage("Name cannot be empty.");
      return;
    }

    if (trimmed.length > MAX_TITLE_LENGTH) {
      setErrorMessage(`Name cannot exceed ${MAX_TITLE_LENGTH} characters.`);
      return;
    }

    setErrorMessage(null);
    onCreateNameChange(trimmed);
  };

  return (
    <div className="flex min-w-[min(12rem,100%)] max-w-full flex-col sm:max-w-[50vw]">
      <div className="flex min-w-0 items-center gap-2">
        <InlineEdit.Root
          key={title}
          initialValue={title.trim() || CREATE_PLACEHOLDER}
          onEdit={handleEdit}
          onCancel={() => setErrorMessage(null)}
          className="min-w-0 flex-1 p-0"
        >
          <InlineEdit.Trigger className="min-w-0">
            <OverflowDisplayTooltip content={displayTitle}>
              <h1
                className={`${getTitleClasses(displayTitle)} ${!title.trim() ? "text-gray-400" : ""}`}
              >
                {displayTitle}
              </h1>
            </OverflowDisplayTooltip>
          </InlineEdit.Trigger>
          <InlineEdit.EditTrigger>
            <PencilIcon className="h-4 w-4" />
          </InlineEdit.EditTrigger>
          <InlineEdit.Input className="whitespace-pre-wrap py-1 text-base" />
          <InlineEdit.Rename>Rename</InlineEdit.Rename>
          <InlineEdit.Cancel>Cancel</InlineEdit.Cancel>
        </InlineEdit.Root>
      </div>
      {errorMessage ? (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
};

const SignalFormTitle = ({
  mode,
  title,
  onTitleEdit,
  onCreateNameChange,
}: SignalFormTitleProps) => {
  if (mode === "edit" && onTitleEdit) {
    return <Title key={title} title={title} onTitleEdit={onTitleEdit} />;
  }

  return <CreateModeTitle title={title} onCreateNameChange={onCreateNameChange} />;
};

export default SignalFormTitle;
